import { useCallback } from 'react';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

type navigationOptions = {
	openContentInNewTab?: boolean;
	isReplaceAction?: boolean;
	forceReload?: boolean;
};

type useNavigationResult = (url: string, options?: navigationOptions) => void;

/**
 * Extracts the contentId from various URL formats used in template creation.
 * Supports:
 * - Edit V2 pages: /wiki/spaces/{space}/pages/edit-v2/{contentId}
 * - Edit V2 blogs: /wiki/spaces/{space}/blog/edit-v2/{contentId}
 * - Resume drafts: /wiki/pages/resumedraft.action?draftId={contentId}&draftShareId={id}
 *
 * @param url - The URL to parse
 * @returns The numeric contentId as a string, or null if not found or invalid
 */
const parseContentIdFromUrl = (url: string): string | null => {
	let contentId: string | null = null;

	// Try to extract from edit-v2 URLs (path format)
	const editV2Match = url.match(/\/edit-v2\/(\d+)/);
	if (editV2Match) {
		contentId = editV2Match[1];
	} else {
		// Try to extract from resumedraft URLs (query param format)
		const draftIdMatch = url.match(/[?&]draftId=(\d+)/);
		if (draftIdMatch) {
			contentId = draftIdMatch[1];
		}
	}

	// Validate that the extracted contentId is purely numerical
	if (contentId && /^\d+$/.test(contentId)) {
		return contentId;
	}

	return null;
};

export const useNavigation = (): useNavigationResult => {
	const { push, replace } = useRouteActions();

	return useCallback(
		(url: string, options?: navigationOptions) => {
			const { openContentInNewTab, isReplaceAction, forceReload } = options || {};

			// Track when we're navigating after template creation.
			if (expValEquals('platform_editor_template_creation_ufo_attribute', 'isEnabled', true)) {
				const contentId = parseContentIdFromUrl(url);

				if (contentId) {
					const templateKey = fg('confluence_browser_storage_prepended_keys')
						? `${contentId}-${keys.PAGE_CREATED_FROM_TEMPLATE}`
						: `${keys.PAGE_CREATED_FROM_TEMPLATE_PREFIX}${contentId}`;
					localStorage.setItem(templateKey, Date.now().toString());
				}
			}

			if (openContentInNewTab) {
				window.open(url, '_blank', 'noreferrer');
			} else if (isReplaceAction) {
				replace(url, forceReload);
			} else {
				push(url, forceReload);
			}
		},
		[push, replace],
	);
};
